import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import BrandLogo from './components/BrandLogo';
import { api, authHeaders } from './api/client';
import { formatCurrency, localeForLanguage } from './lib/format';
import { AuthProvider, useAuth } from './auth/AuthContext';
import ProtectedRoute from './auth/ProtectedRoute';
import CareersPage from './pages/CareersPage';
import SignInPage from './pages/SignInPage';
import StatusPage from './pages/StatusPage';
import InboxPage from './pages/InboxPage';
import ReportsPage from './pages/ReportsPage';
import RecruitingPage from './pages/RecruitingPage';
import { AdminUsersCard, AdminApiKeysCard, AdminCompaniesCard, AdminCompanyBankingCard, AdminCustomerOnboardingCard, AdminLicensesCard } from './pages/AdminPage';
import { BenefitTypesCard, EmployeeBenefitsCard } from './pages/BenefitsPage';
import { EnterpriseLeaveQueueCard, LoanRegisterCard } from './pages/LeavePage';
import {
  SelfProfileCard,
  SelfLeaveRequestCard,
  MyLeaveRequestsCard,
  SelfAttendanceCard,
  MyPerformanceCard,
  MyPayrollHistoryCard,
  MyLoansCard,
} from './pages/MePage';
import {
  EmployeeManagementCard,
  PositionManagementCard,
  PositionsListCard,
  EmployeesListCard,
  TimesheetsCard,
  PerformanceCyclesCard,
  LearningComplianceCard,
  EmployeeDocumentsCard,
} from './pages/PeoplePage';
import { RunPayrollCard, PayrollHistoryCard, CompensationPlanningCard } from './pages/PayrollPage';
import { ClientPortfolioCard, EnterpriseDashboardCard } from './pages/OverviewPage';
import {
  NotificationsCard,
  ExpensesCard,
  AssetsCard,
  OrgChartCard,
  ShiftsCard,
  ContractsCard,
  OffboardingCard,
  DisciplinaryCard,
  WorkforceAnalyticsCard,
  ComplianceCard,
} from './pages/EnterprisePage';

const initialForm = {
  employee_id: '',
  basic_salary: 12000,
  taxable_allowance: 0,
  non_taxable_allowance: 0,
  other_deductions: 0,
  payroll_month: '2025-07',
};

export default function App() {
  const { i18n } = useTranslation();
  const activeLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  useEffect(() => {
    document.documentElement.lang = activeLanguage;
  }, [activeLanguage]);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/careers/jobs/:jobId" element={<CareersPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route
            path="/*"
            element={(
              <ProtectedRoute>
                <LegacyDashboard />
              </ProtectedRoute>
            )}
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

function LegacyDashboard() {
  const { t, i18n } = useTranslation();
  const activeLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const { token, currentUser, signOut: authSignOut } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [clientPortfolio, setClientPortfolio] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [batchResults, setBatchResults] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loans, setLoans] = useState([]);
  const [benefitTypes, setBenefitTypes] = useState([]);
  const [employeeBenefits, setEmployeeBenefits] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    legal_name: '',
    tin: '',
    base_currency: 'ETB',
  });
  const [companyBankingForm, setCompanyBankingForm] = useState({
    source_bank_id: '',
    source_branch_id: '',
    source_account_name: '',
    source_account_number: '',
    provider_mode: 'sandbox',
    provider_name: '',
    provider_base_url: '',
    provider_api_key: '',
    routing_policy: 'allow_supported_interbank',
    allow_mobile_money: false,
    is_active: true,
  });
  const [providerApiKeyConfigured, setProviderApiKeyConfigured] = useState(false);
  const [banks, setBanks] = useState([]);
  const [bankBranches, setBankBranches] = useState([]);
  const [customerOnboardingForm, setCustomerOnboardingForm] = useState({
    company_name: '',
    legal_name: '',
    tin: '',
    base_currency: 'ETB',
    admin_username: '',
    admin_full_name: '',
    admin_password: '',
    seats: '25',
    expires_at: '',
    license_notes: '',
  });
  const [requisitions, setRequisitions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [offers, setOffers] = useState([]);
  const [onboardingHandoffs, setOnboardingHandoffs] = useState([]);
  const [selectedOnboardingHandoffId, setSelectedOnboardingHandoffId] = useState('');
  const [onboardingTasks, setOnboardingTasks] = useState([]);
  const [onboardingDocuments, setOnboardingDocuments] = useState([]);
  const [onboardingChecklistTemplates, setOnboardingChecklistTemplates] = useState([
    { title: 'Collect signed offer letter', due_days_offset: -7, sort_order: 0, is_required: true, acknowledgement_statement: 'I confirm that I have reviewed and accepted the signed offer letter for my employment.', acknowledgement_version: 'v1' },
    { title: 'Collect identity and tax documents', due_days_offset: -5, sort_order: 1, is_required: true, acknowledgement_statement: 'I confirm that the identity and tax documents I submitted are accurate and complete.', acknowledgement_version: 'v1' },
    { title: 'Confirm payout details', due_days_offset: -3, sort_order: 2, is_required: true, acknowledgement_statement: 'I confirm that my payout and bank details are correct for salary processing.', acknowledgement_version: 'v1' },
  ]);
  const [onboardingSummary, setOnboardingSummary] = useState(null);
  const [offerLetterTemplate, setOfferLetterTemplate] = useState({
    document_title: 'Offer Letter',
    opening_paragraph: 'Dear {candidate_name},',
    body_paragraph: '{company_name} is pleased to offer you the role of {requisition_title} on a {employment_type} basis in {location}. Your proposed start date is {start_date} and your compensation will be {compensation}.',
    closing_paragraph: 'Please review this offer and respond before {offer_expiry}. We look forward to welcoming you to {company_name}.',
    signatory_name: 'HR Team',
    signatory_title: 'Human Resources',
  });
  const [apiKeys, setApiKeys] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [generatedApiKey, setGeneratedApiKey] = useState('');
  const [generatedLicenseKey, setGeneratedLicenseKey] = useState('');
  const [selfProfile, setSelfProfile] = useState(null);
  const [selfHistory, setSelfHistory] = useState([]);
  const [selfLeaveRequests, setSelfLeaveRequests] = useState([]);
  const [selfAttendance, setSelfAttendance] = useState([]);
  const [selfPerformanceGoals, setSelfPerformanceGoals] = useState([]);
  const [selfPerformanceReviews, setSelfPerformanceReviews] = useState([]);
  const [selfLoans, setSelfLoans] = useState([]);
  const [recruitingApiAvailable, setRecruitingApiAvailable] = useState(null);
  const [managerLeaveRequests, setManagerLeaveRequests] = useState([]);
  const [managerTeamSummary, setManagerTeamSummary] = useState(null);
  const [managerTimesheets, setManagerTimesheets] = useState([]);
  const [managerTeamMembers, setManagerTeamMembers] = useState([]);
  const [performanceCycles, setPerformanceCycles] = useState([]);
  const [allPerformanceCycles, setAllPerformanceCycles] = useState([]);
  const [managerPerformanceGoals, setManagerPerformanceGoals] = useState([]);
  const [managerPerformanceReviews, setManagerPerformanceReviews] = useState([]);
  const [compensationCycles, setCompensationCycles] = useState([]);
  const [compensationAdjustments, setCompensationAdjustments] = useState([]);
  const [managerCompensationAdjustments, setManagerCompensationAdjustments] = useState([]);
  const [trainingCourses, setTrainingCourses] = useState([]);
  const [trainingAssignments, setTrainingAssignments] = useState([]);
  const [employeeDocuments, setEmployeeDocuments] = useState([]);
  const [employeeForm, setEmployeeForm] = useState({
    full_name: 'Abebe Kebede',
    employee_code: 'ETH-001',
    department: 'Finance',
    position: 'Accountant',
    position_id: '',
    tax_id: 'TIN-001',
    basic_salary: 12000,
    taxable_allowance: 1500,
    non_taxable_allowance: 500,
    other_deductions: 250,
    account_holder_name: 'Abebe Kebede',
    payout_method: 'bank_transfer',
    bank_name: 'Commercial Bank of Ethiopia',
    bank_account_number: '1000123456789',
    bank_branch: 'Addis Ababa',
    mobile_wallet_number: '',
    autopay_enabled: true,
  });
  const [form, setForm] = useState(initialForm);
  const [result, setResult] = useState(null);
  const [resultRecordId, setResultRecordId] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmployeeSubmitting, setIsEmployeeSubmitting] = useState(false);
  const [isUserSubmitting, setIsUserSubmitting] = useState(false);
  const [isCompanySubmitting, setIsCompanySubmitting] = useState(false);
  const [isCompanyBankingSubmitting, setIsCompanyBankingSubmitting] = useState(false);
  const [isSelfBankingSubmitting, setIsSelfBankingSubmitting] = useState(false);
  const [isPositionSubmitting, setIsPositionSubmitting] = useState(false);
  const [isCustomerOnboardingSubmitting, setIsCustomerOnboardingSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isBenefitTypeSubmitting, setIsBenefitTypeSubmitting] = useState(false);
  const [isEmployeeBenefitSubmitting, setIsEmployeeBenefitSubmitting] = useState(false);
  const [isReportSubmitting, setIsReportSubmitting] = useState(false);
  const [isRecruitingSubmitting, setIsRecruitingSubmitting] = useState(false);
  const [isCandidateSubmitting, setIsCandidateSubmitting] = useState(false);
  const [isApplicationSubmitting, setIsApplicationSubmitting] = useState(false);
  const [isApplicationStageSubmitting, setIsApplicationStageSubmitting] = useState(false);
  const [isOfferSubmitting, setIsOfferSubmitting] = useState(false);
  const [isOfferTemplateSubmitting, setIsOfferTemplateSubmitting] = useState(false);
  const [isOnboardingTemplateSubmitting, setIsOnboardingTemplateSubmitting] = useState(false);
  const [isOnboardingTaskSubmitting, setIsOnboardingTaskSubmitting] = useState(false);
  const [isOnboardingDocumentSubmitting, setIsOnboardingDocumentSubmitting] = useState(false);
  const [isOnboardingUserSubmitting, setIsOnboardingUserSubmitting] = useState(false);
  const [isApiKeySubmitting, setIsApiKeySubmitting] = useState(false);
  const [isLicenseSubmitting, setIsLicenseSubmitting] = useState(false);
  const [isMfaSubmitting, setIsMfaSubmitting] = useState(false);
  const [isTimesheetImporting, setIsTimesheetImporting] = useState(false);
  const [isSelfLeaveSubmitting, setIsSelfLeaveSubmitting] = useState(false);
  const [isSelfAttendanceSubmitting, setIsSelfAttendanceSubmitting] = useState(false);
  const [isSelfPerformanceSubmitting, setIsSelfPerformanceSubmitting] = useState(false);
  const [isPerformanceSubmitting, setIsPerformanceSubmitting] = useState(false);
  const [isPerformanceCycleSubmitting, setIsPerformanceCycleSubmitting] = useState(false);
  const [isCompensationCycleSubmitting, setIsCompensationCycleSubmitting] = useState(false);
  const [isCompensationSubmitting, setIsCompensationSubmitting] = useState(false);
  const [isTrainingCourseSubmitting, setIsTrainingCourseSubmitting] = useState(false);
  const [isTrainingAssignmentSubmitting, setIsTrainingAssignmentSubmitting] = useState(false);
  const [isEmployeeDocumentSubmitting, setIsEmployeeDocumentSubmitting] = useState(false);
  const [isSelfOnboardingSubmitting, setIsSelfOnboardingSubmitting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [timesheetImportFile, setTimesheetImportFile] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const activePage = (() => {
    const seg = location.pathname.replace(/^\/+/, '').split('/')[0];
    return seg || 'overview';
  })();
  const [theme, setTheme] = useState(() => window.localStorage.getItem('afrihr.theme') || 'auto');
  const [userForm, setUserForm] = useState({
    username: '',
    full_name: '',
    password: '',
    role: 'employee',
    employee_id: '',
    primary_company_id: '',
    company_ids: [],
  });
  const [selfLeaveForm, setSelfLeaveForm] = useState({
    leave_type_id: '',
    start_date: '2026-05-10',
    end_date: '2026-05-12',
    reason: '',
  });
  const [selfBankingForm, setSelfBankingForm] = useState({
    account_holder_name: '',
    payout_method: 'bank_transfer',
    bank_id: '',
    bank_branch_id: '',
    bank_account_number: '',
    mobile_wallet_number: '',
    autopay_enabled: false,
  });
  const [performanceGoalForm, setPerformanceGoalForm] = useState({
    employee_id: '',
    cycle_id: '',
    title: '',
    metric: '',
    target_value: '',
    weight: '25',
    due_date: '',
    description: '',
  });
  const [performanceReviewForm, setPerformanceReviewForm] = useState({
    employee_id: '',
    cycle_id: '',
    overall_rating: '3',
    manager_comments: '',
  });
  const [performanceCycleForm, setPerformanceCycleForm] = useState({
    name: '',
    period_start: '2026-01-01',
    period_end: '2026-12-31',
    status: 'active',
  });
  const [compensationCycleForm, setCompensationCycleForm] = useState({
    name: '',
    effective_date: '2026-07-01',
    budget_amount: 0,
    status: 'active',
  });
  const [compensationAdjustmentForm, setCompensationAdjustmentForm] = useState({
    employee_id: '',
    cycle_id: '',
    proposed_basic_salary: '',
    proposed_taxable_allowance: 0,
    lump_sum_bonus: 0,
    rationale: '',
  });
  const [trainingCourseForm, setTrainingCourseForm] = useState({
    title: '',
    category: 'compliance',
    description: '',
    required_by_default: true,
    due_days_after_assignment: '30',
    renewal_months: '12',
    is_active: true,
  });
  const [trainingAssignmentForm, setTrainingAssignmentForm] = useState({
    course_id: '',
    employee_id: '',
    due_date: '',
    notes: '',
  });
  const [employeeDocumentForm, setEmployeeDocumentForm] = useState({
    employee_id: '',
    document_name: '',
    category: 'policy',
    issued_at: '',
    expires_at: '',
    requires_acknowledgement: false,
    acknowledgement_statement: '',
    acknowledgement_version: 'v1',
    file: null,
  });
  const [selfBankBranches, setSelfBankBranches] = useState([]);
  const [selfOnboardingDocumentForm, setSelfOnboardingDocumentForm] = useState({
    document_name: '',
    task_id: '',
    file: null,
  });
  const [positionForm, setPositionForm] = useState({
    job_code: '',
    title: '',
    department_id: '',
    grade: '',
    min_salary: 10000,
    max_salary: 15000,
    headcount: 1,
  });
  const [benefitTypeForm, setBenefitTypeForm] = useState({
    name: '',
    amount: 0,
    is_taxable: true,
    formula: '',
  });
  const [employeeBenefitForm, setEmployeeBenefitForm] = useState({
    employee_id: '',
    benefit_type_id: '',
    amount: 0,
    start_date: '',
    end_date: '',
  });
  const [savedReportForm, setSavedReportForm] = useState({
    name: '',
    filters: '{"month":"2026-05"}',
    columns: 'employee_name, net_pay',
  });
  const [recruitingForm, setRecruitingForm] = useState({
    slug: '',
    title: '',
    department_id: '',
    position_id: '',
    employment_type: 'full_time',
    location: '',
    headcount: 1,
    budgeted_salary_min: 0,
    budgeted_salary_max: 0,
    target_start_date: '',
    description: '',
  });
  const [candidateForm, setCandidateForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    source: '',
    current_company: '',
    current_title: '',
    resume_url: '',
  });
  const [applicationForm, setApplicationForm] = useState({
    requisition_id: '',
    candidate_id: '',
    score: '',
    notes_summary: '',
  });
  const [applicationStageForm, setApplicationStageForm] = useState({
    application_id: '',
    stage: 'screening',
    score: '',
    notes_summary: '',
    interview_scheduled_at: '',
    evaluator_notes: '',
    rejection_reason: '',
  });
  const [offerForm, setOfferForm] = useState({
    application_id: '',
    compensation_amount: '',
    currency: 'ETB',
    proposed_start_date: '',
    expires_at: '',
    notes_summary: '',
    message: '',
  });
  const [onboardingTaskForm, setOnboardingTaskForm] = useState({
    title: '',
    due_date: '',
  });
  const [onboardingUserForm, setOnboardingUserForm] = useState({
    username: '',
    password: '',
    full_name: '',
  });
  const [onboardingDocumentForm, setOnboardingDocumentForm] = useState({
    document_name: '',
    task_id: '',
    file: null,
  });
  const [apiKeyForm, setApiKeyForm] = useState({ name: '' });
  const [licenseForm, setLicenseForm] = useState({ company_id: '', seats: '25', expires_at: '', notes: '' });
  const [timesheetEmployeeId, setTimesheetEmployeeId] = useState('');

  const selectedEmployee = useMemo(
    () => employees.find((employee) => employee.id === Number(form.employee_id)) || null,
    [employees, form.employee_id],
  );
  const currentRole = currentUser?.role || '';
  const systemAdminRoles = ['admin', 'system_admin'];
  const adminLikeRoles = [...systemAdminRoles, 'company_admin'];
  const isSystemAdmin = systemAdminRoles.includes(currentRole);
  const canSwitchCompanies = isSystemAdmin || ['accountant', 'auditor'].includes(currentRole);
  const canViewPortfolio = adminLikeRoles.includes(currentRole) || ['accountant', 'auditor'].includes(currentRole);
  const canRunFinanceExports = adminLikeRoles.includes(currentRole) || ['finance', 'accountant'].includes(currentRole);
  const canManageEmployees = adminLikeRoles.includes(currentRole) || currentRole === 'hr';
  const canManageRecruiting = adminLikeRoles.includes(currentRole) || currentRole === 'hr';
  const canApproveRecruiting = adminLikeRoles.includes(currentRole) || ['hr', 'finance'].includes(currentRole);
  const canViewRecruiting = adminLikeRoles.includes(currentRole) || ['hr', 'finance', 'manager'].includes(currentRole);
  const canManageUsers = adminLikeRoles.includes(currentRole);
  const canManageCompanies = isSystemAdmin;
  const canManageLicenses = isSystemAdmin;
  const canProcessPayroll = adminLikeRoles.includes(currentRole) || currentRole === 'hr';
  const canViewFinance = adminLikeRoles.includes(currentRole) || ['hr', 'finance', 'accountant', 'auditor'].includes(currentRole);
  const canManageCompensation = adminLikeRoles.includes(currentRole) || ['hr', 'finance'].includes(currentRole);
  const canViewEnterpriseOps = adminLikeRoles.includes(currentRole) || ['hr', 'finance'].includes(currentRole);
  const canUseSelfService = ['employee', 'manager'].includes(currentRole);
  const canReviewManagerQueues = currentRole === 'manager';
  const formatRoleLabel = (role) => {
    if (role === 'company_admin') return 'Company admin';
    if (role === 'system_admin' || role === 'admin') return 'System admin';
    return role;
  };

  const navPages = useMemo(() => {
    const list = [];
    if (canViewPortfolio || canViewEnterpriseOps) list.push({ id: 'overview', label: t('navOverview') });
    if (canManageEmployees || canViewEnterpriseOps) list.push({ id: 'people', label: t('navPeople') });
    if (canManageEmployees) list.push({ id: 'learning', label: t('navLearning') });
    if (canViewRecruiting) list.push({ id: 'recruiting', label: 'Recruiting' });
    if (canProcessPayroll || canViewFinance) list.push({ id: 'payroll', label: t('navPayroll') });
    if (canViewEnterpriseOps) list.push({ id: 'leave', label: t('navLeaveLoans') });
    if (canManageEmployees) list.push({ id: 'benefits', label: t('navBenefits') });
    if (canViewFinance) list.push({ id: 'reports', label: t('navReports') });
    if (canReviewManagerQueues) list.push({ id: 'inbox', label: t('navApprovals') });
    if (canManageEmployees || canViewEnterpriseOps) list.push({ id: 'enterprise', label: t('navEnterprise') });
    if (canUseSelfService || canViewFinance) list.push({ id: 'expenses', label: t('navExpenses') });
    if (canUseSelfService) list.push({ id: 'me', label: t('navMyWorkspace') });
    if (canManageUsers) list.push({ id: 'admin', label: t('navAdministration') });
    return list;
  }, [t, canViewPortfolio, canViewEnterpriseOps, canManageEmployees, canViewRecruiting, canProcessPayroll, canViewFinance, canReviewManagerQueues, canManageEmployees, canUseSelfService, canManageUsers]);

  useEffect(() => {
    if (!navPages.length) return;
    if (!navPages.find((page) => page.id === activePage)) {
      navigate(`/${navPages[0].id}`, { replace: true });
    }
  }, [navPages, activePage, navigate]);

  useEffect(() => {
    if (theme === 'auto') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
    }
    window.localStorage.setItem('afrihr.theme', theme);
  }, [theme]);

  const activeCompanyId = canSwitchCompanies
    ? Number(selectedCompanyId || currentUser?.company_id || 0) || null
    : currentUser?.company_id || null;
  const activeCompanyName = companies.find((company) => company.id === Number(activeCompanyId))?.name || '';

  useEffect(() => {
    if (!token || !adminLikeRoles.includes(currentRole) || !companyBankingForm.source_bank_id) {
      setBankBranches([]);
      return;
    }

    let ignore = false;
    api.get(`/api/banks/${companyBankingForm.source_bank_id}/branches`, authHeaders(token))
      .then((response) => {
        if (!ignore) {
          setBankBranches(response.data);
        }
      })
      .catch((requestError) => {
        if (!ignore) {
          setError(requestError.response?.data?.detail || requestError.message);
          setBankBranches([]);
        }
      });

    return () => {
      ignore = true;
    };
  }, [token, currentRole, companyBankingForm.source_bank_id]);

  useEffect(() => {
    if (!token || !canUseSelfService || !selfBankingForm.bank_id) {
      setSelfBankBranches([]);
      return;
    }

    let ignore = false;
    api.get(`/api/banks/${selfBankingForm.bank_id}/branches`, authHeaders(token))
      .then((response) => {
        if (!ignore) {
          setSelfBankBranches(response.data);
        }
      })
      .catch((requestError) => {
        if (!ignore) {
          setError(requestError.response?.data?.detail || requestError.message);
          setSelfBankBranches([]);
        }
      });

    return () => {
      ignore = true;
    };
  }, [token, canUseSelfService, selfBankingForm.bank_id]);

  useEffect(() => {
    if (!banks.length || !selfProfile?.employee?.bank_name || selfBankingForm.bank_id) {
      return;
    }

    const targetBankName = String(selfProfile.employee.bank_name).toLowerCase();
    const matchedBank = banks.find((bank) => (
      [bank.name, bank.short_name, bank.code].some((value) => String(value || '').toLowerCase() === targetBankName)
    ));

    if (!matchedBank) {
      return;
    }

    setSelfBankingForm((current) => ({
      ...current,
      bank_id: String(matchedBank.id),
    }));
  }, [banks, selfProfile, selfBankingForm.bank_id]);

  useEffect(() => {
    if (!token || !canViewEnterpriseOps || !employeeBenefitForm.employee_id) {
      setEmployeeBenefits([]);
      return;
    }
    void loadEmployeeBenefits(Number(employeeBenefitForm.employee_id));
  }, [token, canViewEnterpriseOps, employeeBenefitForm.employee_id, activeCompanyId]);

  useEffect(() => {
    if (!token || !timesheetEmployeeId) {
      setTimesheets([]);
      return;
    }
    void loadTimesheets(Number(timesheetEmployeeId));
  }, [token, timesheetEmployeeId, activeCompanyId]);

  useEffect(() => {
    let cancelled = false;

    if (!token || !currentUser) {
      setRecruitingApiAvailable(null);
      return undefined;
    }

    if (![...adminLikeRoles, 'hr', 'finance', 'manager'].includes(currentUser.role)) {
      setRecruitingApiAvailable(false);
      return undefined;
    }

    setRecruitingApiAvailable(null);

    async function detectRecruitingApi() {
      try {
        const response = await api.get('/openapi.json');
        const paths = response.data?.paths || {};
        const isAvailable = Object.keys(paths).some((path) => path.startsWith('/api/recruiting/'));
        if (!cancelled) {
          setRecruitingApiAvailable(isAvailable);
        }
      } catch {
        if (!cancelled) {
          setRecruitingApiAvailable(false);
        }
      }
    }

    void detectRecruitingApi();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, currentUser?.role, token]);

  // Once AuthContext hydrates currentUser, load the dashboard payload for this session.
  useEffect(() => {
    if (!currentUser || !token) {
      return;
    }
    if ([...adminLikeRoles, 'hr', 'finance', 'manager'].includes(currentUser.role) && recruitingApiAvailable === null) {
      return;
    }
    const company = [...adminLikeRoles, 'accountant', 'auditor'].includes(currentUser.role)
      ? String(currentUser.company_id)
      : '';
    setSelectedCompanyId(company);
    void loadDashboard(token, currentUser, company);
  }, [currentUser?.id, currentUser?.role, token, recruitingApiAvailable]);

  async function downloadPayoutCsv() {
    try {
      const response = await api.get('/api/payroll/reports/payouts.csv', {
        ...authHeaders(token),
        params: buildRequestParams(),
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `payouts-${form.payroll_month || 'report'}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function loadDashboard(sessionToken = token, user = currentUser, companyOverride = selectedCompanyId) {
    const role = user?.role || '';
    const requests = [];
    const applyResults = [];
    const companyParams = [...adminLikeRoles, 'accountant', 'auditor'].includes(role) && companyOverride
      ? { company_id: Number(companyOverride) }
      : undefined;

    function queueDashboardRequest(request, apply, fallbackData) {
      if (fallbackData !== undefined) {
        requests.push(
          request.catch((requestError) => {
            const statusCode = requestError.response?.status;
            if (statusCode === 403 || statusCode === 404) {
              return { data: fallbackData };
            }
            throw requestError;
          }),
        );
      } else {
        requests.push(request);
      }
      applyResults.push((response) => apply(response.data));
    }

    queueDashboardRequest(api.get('/api/leave/types', authHeaders(sessionToken)), (data) => setLeaveTypes(data));

    if ([...adminLikeRoles, 'hr', 'finance'].includes(role)) {
      queueDashboardRequest(api.get('/api/dashboard', authHeaders(sessionToken, companyParams)), (data) => setDashboardMetrics(data));
      queueDashboardRequest(api.get('/api/positions', authHeaders(sessionToken, companyParams)), (data) => setPositions(data));
      queueDashboardRequest(api.get('/api/departments', authHeaders(sessionToken, companyParams)), (data) => setDepartments(data));
      queueDashboardRequest(api.get('/api/employees', authHeaders(sessionToken, companyParams)), (data) => setEmployees(data));
      queueDashboardRequest(api.get('/api/leave/requests', authHeaders(sessionToken, companyParams)), (data) => setLeaveRequests(data));
      queueDashboardRequest(api.get('/api/loans', authHeaders(sessionToken, companyParams)), (data) => setLoans(data));
      queueDashboardRequest(api.get('/api/benefits/types', authHeaders(sessionToken, companyParams)), (data) => setBenefitTypes(data));
      queueDashboardRequest(api.get('/api/reports/saved', authHeaders(sessionToken, companyParams)), (data) => setSavedReports(data));
      queueDashboardRequest(api.get('/api/performance/cycles', authHeaders(sessionToken, companyParams)), (data) => setAllPerformanceCycles(data), []);
      queueDashboardRequest(api.get('/api/compensation/cycles', authHeaders(sessionToken, companyParams)), (data) => setCompensationCycles(data), []);
      queueDashboardRequest(api.get('/api/compensation/adjustments', authHeaders(sessionToken, companyParams)), (data) => setCompensationAdjustments(data), []);
      queueDashboardRequest(api.get('/api/learning/courses', authHeaders(sessionToken, companyParams)), (data) => setTrainingCourses(data), []);
      queueDashboardRequest(api.get('/api/learning/assignments', authHeaders(sessionToken, companyParams)), (data) => setTrainingAssignments(data), []);
      queueDashboardRequest(api.get('/api/employee-documents', authHeaders(sessionToken, companyParams)), (data) => setEmployeeDocuments(data), []);
    } else {
      setDashboardMetrics(null);
      setPositions([]);
      setDepartments([]);
      setEmployees([]);
      setHistory([]);
      setLeaveRequests([]);
      setLoans([]);
      setBenefitTypes([]);
      setSavedReports([]);
      setAllPerformanceCycles([]);
      setCompensationCycles([]);
      setCompensationAdjustments([]);
      setTrainingCourses([]);
      setTrainingAssignments([]);
      setEmployeeDocuments([]);
    }

    if ([...adminLikeRoles, 'hr', 'finance', 'manager'].includes(role) && recruitingApiAvailable) {
      queueDashboardRequest(api.get('/api/recruiting/requisitions', authHeaders(sessionToken, companyParams)), (data) => setRequisitions(data), []);
      queueDashboardRequest(api.get('/api/recruiting/candidates', authHeaders(sessionToken, companyParams)), (data) => setCandidates(data), []);
      queueDashboardRequest(api.get('/api/recruiting/applications', authHeaders(sessionToken, companyParams)), (data) => setApplications(data), []);
      queueDashboardRequest(api.get('/api/recruiting/offers', authHeaders(sessionToken, companyParams)), (data) => setOffers(data), []);
      queueDashboardRequest(api.get('/api/recruiting/onboarding-handoffs', authHeaders(sessionToken, companyParams)), (data) => setOnboardingHandoffs(data), []);
      queueDashboardRequest(api.get('/api/recruiting/onboarding-checklist-templates', authHeaders(sessionToken, companyParams)), (data) => setOnboardingChecklistTemplates(data), onboardingChecklistTemplates);
      queueDashboardRequest(api.get('/api/recruiting/onboarding-summary', authHeaders(sessionToken, companyParams)), (data) => setOnboardingSummary(data), null);
      queueDashboardRequest(api.get('/api/recruiting/offer-letter-template', authHeaders(sessionToken, companyParams)), (data) => setOfferLetterTemplate(data), offerLetterTemplate);
    } else {
      setRequisitions([]);
      setCandidates([]);
      setApplications([]);
      setOffers([]);
      setOnboardingHandoffs([]);
      setSelectedOnboardingHandoffId('');
      setOnboardingTasks([]);
      setOnboardingDocuments([]);
      setOnboardingChecklistTemplates([
        { title: 'Collect signed offer letter', due_days_offset: -7, sort_order: 0, is_required: true, acknowledgement_statement: 'I confirm that I have reviewed and accepted the signed offer letter for my employment.', acknowledgement_version: 'v1' },
        { title: 'Collect identity and tax documents', due_days_offset: -5, sort_order: 1, is_required: true, acknowledgement_statement: 'I confirm that the identity and tax documents I submitted are accurate and complete.', acknowledgement_version: 'v1' },
        { title: 'Confirm payout details', due_days_offset: -3, sort_order: 2, is_required: true, acknowledgement_statement: 'I confirm that my payout and bank details are correct for salary processing.', acknowledgement_version: 'v1' },
      ]);
      setOnboardingSummary(null);
      setOfferLetterTemplate({
        document_title: 'Offer Letter',
        opening_paragraph: 'Dear {candidate_name},',
        body_paragraph: '{company_name} is pleased to offer you the role of {requisition_title} on a {employment_type} basis in {location}. Your proposed start date is {start_date} and your compensation will be {compensation}.',
        closing_paragraph: 'Please review this offer and respond before {offer_expiry}. We look forward to welcoming you to {company_name}.',
        signatory_name: 'HR Team',
        signatory_title: 'Human Resources',
      });
    }

    if ([...adminLikeRoles, 'hr', 'finance', 'accountant', 'auditor'].includes(role)) {
      queueDashboardRequest(api.get('/api/payroll/history', authHeaders(sessionToken, companyParams)), (data) => setHistory(data));
    } else {
      setHistory([]);
    }

    if ([...adminLikeRoles, 'accountant', 'auditor'].includes(role)) {
      queueDashboardRequest(api.get('/api/audit-logs', authHeaders(sessionToken, companyParams)), (data) => setAuditLogs(data));
    } else {
      setAuditLogs([]);
    }

    if ([...adminLikeRoles, 'accountant', 'auditor'].includes(role)) {
      queueDashboardRequest(api.get('/api/companies', authHeaders(sessionToken)), (data) => setCompanies(data));
      queueDashboardRequest(api.get('/api/portfolio/clients', authHeaders(sessionToken)), (data) => setClientPortfolio(data));
    } else {
      setCompanies([]);
      setClientPortfolio([]);
    }

    if (adminLikeRoles.includes(role)) {
      queueDashboardRequest(api.get('/api/users', authHeaders(sessionToken, companyParams)), (data) => setUsers(data));
      queueDashboardRequest(api.get('/api/admin/api-keys', authHeaders(sessionToken, companyParams)), (data) => setApiKeys(data));
      queueDashboardRequest(api.get('/api/banks', authHeaders(sessionToken)), (data) => setBanks(data));
      queueDashboardRequest(api.get('/api/company-banking', authHeaders(sessionToken, companyParams)), (data) => {
        setCompanyBankingForm({
          source_bank_id: data?.source_bank_id ? String(data.source_bank_id) : '',
          source_branch_id: data?.source_branch_id ? String(data.source_branch_id) : '',
          source_account_name: data?.source_account_name || '',
          source_account_number: '',
          provider_mode: data?.provider_mode || 'sandbox',
          provider_name: data?.provider_name || '',
          provider_base_url: data?.provider_base_url || '',
          provider_api_key: '',
          routing_policy: data?.routing_policy || 'allow_supported_interbank',
          allow_mobile_money: Boolean(data?.allow_mobile_money),
          is_active: data?.is_active !== false,
        });
        setProviderApiKeyConfigured(Boolean(data?.provider_api_key_configured));
      });
      if (systemAdminRoles.includes(role)) {
        queueDashboardRequest(api.get('/api/admin/licenses', authHeaders(sessionToken)), (data) => setLicenses(data));
      } else {
        setLicenses([]);
      }
    } else {
      setUsers([]);
      setApiKeys([]);
      setLicenses([]);
      setBanks([]);
      setBankBranches([]);
      setProviderApiKeyConfigured(false);
    }

    if (['employee', 'manager'].includes(role)) {
      queueDashboardRequest(api.get('/api/banks', authHeaders(sessionToken)), (data) => setBanks(data), []);
      queueDashboardRequest(api.get('/api/self-service/profile', authHeaders(sessionToken)), (data) => {
        setSelfProfile(data);
        setSelfBankingForm({
          account_holder_name: data?.employee?.account_holder_name || data?.employee?.full_name || '',
          payout_method: data?.employee?.payout_method || 'bank_transfer',
          bank_id: data?.employee?.bank_id ? String(data.employee.bank_id) : '',
          bank_branch_id: data?.employee?.bank_branch_id ? String(data.employee.bank_branch_id) : '',
          bank_account_number: data?.employee?.bank_account_number || '',
          mobile_wallet_number: data?.employee?.mobile_wallet_number || '',
          autopay_enabled: Boolean(data?.employee?.autopay_enabled),
        });
        if (data?.employee?.id) {
          setTimesheetEmployeeId(String(data.employee.id));
        } else {
          setTimesheetEmployeeId('');
        }
      }, null);
      queueDashboardRequest(api.get('/api/self/payroll/history', authHeaders(sessionToken)), (data) => setSelfHistory(data), []);
      queueDashboardRequest(api.get('/api/self/leave/requests', authHeaders(sessionToken)), (data) => setSelfLeaveRequests(data), []);
      queueDashboardRequest(api.get('/api/self/attendance', authHeaders(sessionToken)), (data) => setSelfAttendance(data), []);
      queueDashboardRequest(api.get('/api/self/performance/goals', authHeaders(sessionToken)), (data) => setSelfPerformanceGoals(data), []);
      queueDashboardRequest(api.get('/api/self/performance/reviews', authHeaders(sessionToken)), (data) => setSelfPerformanceReviews(data), []);
      queueDashboardRequest(api.get('/api/self/loans', authHeaders(sessionToken)), (data) => setSelfLoans(data), []);
    } else {
      setSelfProfile(null);
      setSelfBankingForm({
        account_holder_name: '',
        payout_method: 'bank_transfer',
        bank_id: '',
        bank_branch_id: '',
        bank_account_number: '',
        mobile_wallet_number: '',
        autopay_enabled: false,
      });
      setSelfBankBranches([]);
      setSelfHistory([]);
      setSelfLeaveRequests([]);
      setSelfAttendance([]);
      setSelfPerformanceGoals([]);
      setSelfPerformanceReviews([]);
      setSelfLoans([]);
    }

    if (role === 'manager') {
      queueDashboardRequest(api.get('/api/manager/leave/requests', authHeaders(sessionToken)), (data) => setManagerLeaveRequests(data));
      queueDashboardRequest(api.get('/api/manager/team/summary', authHeaders(sessionToken)), (data) => setManagerTeamSummary(data), null);
      queueDashboardRequest(api.get('/api/manager/attendance/timesheets', authHeaders(sessionToken, { status_filter: 'pending' })), (data) => setManagerTimesheets(data), []);
      queueDashboardRequest(api.get('/api/manager/team/members', authHeaders(sessionToken)), (data) => setManagerTeamMembers(data), []);
      queueDashboardRequest(api.get('/api/performance/cycles', authHeaders(sessionToken, { status_filter: 'active' })), (data) => setPerformanceCycles(data), []);
      queueDashboardRequest(api.get('/api/compensation/cycles', authHeaders(sessionToken, { status_filter: 'active' })), (data) => setCompensationCycles(data), []);
      queueDashboardRequest(api.get('/api/manager/performance/goals', authHeaders(sessionToken)), (data) => setManagerPerformanceGoals(data), []);
      queueDashboardRequest(api.get('/api/manager/performance/reviews', authHeaders(sessionToken)), (data) => setManagerPerformanceReviews(data), []);
      queueDashboardRequest(api.get('/api/manager/compensation/adjustments', authHeaders(sessionToken)), (data) => setManagerCompensationAdjustments(data), []);
    } else {
      setManagerLeaveRequests([]);
      setManagerTeamSummary(null);
      setManagerTimesheets([]);
      setManagerTeamMembers([]);
      setPerformanceCycles([]);
      setManagerPerformanceGoals([]);
      setManagerPerformanceReviews([]);
      setManagerCompensationAdjustments([]);
    }

    const responses = await Promise.all(requests);
    responses.forEach((response, index) => applyResults[index](response));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        employee_id: form.employee_id ? Number(form.employee_id) : null,
        employee_name: selectedEmployee?.full_name || null,
        basic_salary: Number(form.basic_salary),
        taxable_allowance: Number(form.taxable_allowance || 0),
        non_taxable_allowance: Number(form.non_taxable_allowance || 0),
        other_deductions: Number(form.other_deductions || 0),
        payroll_month: form.payroll_month,
      };
      const response = await api.post('/api/payroll/calculate', payload, authHeaders(token));
      setResult(response.data);
      setResultRecordId(response.data.record_id || null);
      setBatchResults([]);
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCreateEmployee(event) {
    event.preventDefault();
    setIsEmployeeSubmitting(true);
    setError('');

    try {
      await api.post(
        '/api/employees',
        {
          ...employeeForm,
          position_id: employeeForm.position_id ? Number(employeeForm.position_id) : null,
          basic_salary: Number(employeeForm.basic_salary),
          taxable_allowance: Number(employeeForm.taxable_allowance || 0),
          non_taxable_allowance: Number(employeeForm.non_taxable_allowance || 0),
          other_deductions: Number(employeeForm.other_deductions || 0),
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      await loadDashboard();
      setEmployeeForm({
        full_name: '',
        employee_code: '',
        department: '',
        position: '',
        position_id: '',
        tax_id: '',
        basic_salary: '',
        taxable_allowance: 0,
        non_taxable_allowance: 0,
        other_deductions: 0,
        account_holder_name: '',
        payout_method: 'bank_transfer',
        bank_name: '',
        bank_account_number: '',
        bank_branch: '',
        mobile_wallet_number: '',
        autopay_enabled: false,
      });
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsEmployeeSubmitting(false);
    }
  }

  async function handleCreatePosition(event) {
    event.preventDefault();
    setIsPositionSubmitting(true);
    setError('');

    try {
      await api.post(
        '/api/positions',
        {
          ...positionForm,
          department_id: positionForm.department_id ? Number(positionForm.department_id) : null,
          min_salary: Number(positionForm.min_salary || 0),
          max_salary: Number(positionForm.max_salary || 0),
          headcount: Number(positionForm.headcount || 1),
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setPositionForm({
        job_code: '',
        title: '',
        department_id: '',
        grade: '',
        min_salary: 10000,
        max_salary: 15000,
        headcount: 1,
      });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsPositionSubmitting(false);
    }
  }

  async function handleCreateUser(event) {
    event.preventDefault();
    setIsUserSubmitting(true);
    setError('');

    try {
      const payload = {
        ...userForm,
        employee_id: userForm.employee_id ? Number(userForm.employee_id) : null,
        company_ids: userForm.company_ids.map((companyId) => Number(companyId)),
          primary_company_id: userForm.primary_company_id ? Number(userForm.primary_company_id) : null,
      };
      if (editingUserId) {
        await api.put(
          `/api/users/${editingUserId}`,
          {
            full_name: payload.full_name,
            password: payload.password || null,
            role: payload.role,
            employee_id: payload.employee_id,
            primary_company_id: payload.primary_company_id,
            company_ids: payload.company_ids,
          },
          authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
        );
      } else {
        await api.post(
          '/api/users',
          payload,
          authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
        );
      }
      resetUserForm();
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsUserSubmitting(false);
    }
  }

  async function handleCreateCompany(event) {
    event.preventDefault();
    setIsCompanySubmitting(true);
    setError('');

    try {
      const response = await api.post(
        '/api/companies',
        {
          name: companyForm.name,
          legal_name: companyForm.legal_name || null,
          tin: companyForm.tin || null,
          base_currency: companyForm.base_currency || 'ETB',
        },
        authHeaders(token),
      );
      const nextCompanyId = String(response.data.id);
      setCompanyForm({ name: '', legal_name: '', tin: '', base_currency: 'ETB' });
      setLicenseForm((current) => ({ ...current, company_id: nextCompanyId }));
      setSelectedCompanyId(nextCompanyId);
      setUserForm((current) => ({ ...current, primary_company_id: nextCompanyId }));
      await loadDashboard(token, currentUser, nextCompanyId);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsCompanySubmitting(false);
    }
  }

  async function handleOnboardCustomer(event) {
    event.preventDefault();
    setIsCustomerOnboardingSubmitting(true);
    setError('');

    try {
      const response = await api.post(
        '/api/admin/customers/onboard',
        {
          ...customerOnboardingForm,
          seats: Number(customerOnboardingForm.seats || 1),
          expires_at: customerOnboardingForm.expires_at || null,
          license_notes: customerOnboardingForm.license_notes || null,
        },
        authHeaders(token),
      );
      const nextCompanyId = String(response.data.company.id);
      setGeneratedLicenseKey(response.data.license_key);
      setCustomerOnboardingForm({
        company_name: '',
        legal_name: '',
        tin: '',
        base_currency: 'ETB',
        admin_username: '',
        admin_full_name: '',
        admin_password: '',
        seats: '25',
        expires_at: '',
        license_notes: '',
      });
      setSelectedCompanyId(nextCompanyId);
      setLicenseForm((current) => ({ ...current, company_id: nextCompanyId }));
      setUserForm((current) => ({ ...current, primary_company_id: nextCompanyId }));
      await loadDashboard(token, currentUser, nextCompanyId);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsCustomerOnboardingSubmitting(false);
    }
  }

  async function handleSaveCompanyBanking(event) {
    event.preventDefault();
    setIsCompanyBankingSubmitting(true);
    setError('');

    try {
      await api.put(
        '/api/company-banking',
        {
          source_bank_id: Number(companyBankingForm.source_bank_id),
          source_branch_id: companyBankingForm.source_branch_id ? Number(companyBankingForm.source_branch_id) : null,
          source_account_name: companyBankingForm.source_account_name,
          source_account_number: companyBankingForm.source_account_number,
          provider_mode: companyBankingForm.provider_mode,
          provider_name: companyBankingForm.provider_name || null,
          provider_base_url: companyBankingForm.provider_base_url || null,
          provider_api_key: companyBankingForm.provider_api_key || undefined,
          routing_policy: companyBankingForm.routing_policy,
          allow_mobile_money: companyBankingForm.allow_mobile_money,
          is_active: companyBankingForm.is_active,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setCompanyBankingForm((current) => ({ ...current, source_account_number: '', provider_api_key: '' }));
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsCompanyBankingSubmitting(false);
    }
  }

  async function handleSaveSelfBanking(event) {
    event.preventDefault();
    setIsSelfBankingSubmitting(true);
    setError('');

    try {
      await api.put(
        '/api/self-service/profile/banking',
        {
          account_holder_name: selfBankingForm.account_holder_name || null,
          payout_method: selfBankingForm.payout_method,
          bank_id: selfBankingForm.payout_method === 'bank_transfer' && selfBankingForm.bank_id ? Number(selfBankingForm.bank_id) : null,
          bank_branch_id: selfBankingForm.payout_method === 'bank_transfer' && selfBankingForm.bank_branch_id ? Number(selfBankingForm.bank_branch_id) : null,
          bank_account_number: selfBankingForm.payout_method === 'bank_transfer' ? selfBankingForm.bank_account_number || null : null,
          mobile_wallet_number: selfBankingForm.payout_method === 'mobile_money' ? selfBankingForm.mobile_wallet_number || null : null,
          autopay_enabled: selfBankingForm.autopay_enabled,
        },
        authHeaders(token),
      );
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsSelfBankingSubmitting(false);
    }
  }

  async function handleBatchRun() {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await api.post(
        '/api/payroll/run-batch',
        { payroll_month: form.payroll_month },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setBatchResults(response.data);
      setResult(null);
      setResultRecordId(null);
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleBulkImport() {
    if (!importFile) {
      setError(t('chooseFile'));
      return;
    }

    setIsImporting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      await api.post('/api/employees/bulk-import', formData, authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined));
      setImportFile(null);
      await loadDashboard();
      window.alert(t('importComplete'));
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsImporting(false);
    }
  }

  async function handleSelfLeaveRequest(event) {
    event.preventDefault();
    setIsSelfLeaveSubmitting(true);
    setError('');

    try {
      await api.post(
        '/api/self/leave/requests',
        {
          ...selfLeaveForm,
          leave_type_id: Number(selfLeaveForm.leave_type_id),
        },
        authHeaders(token),
      );
      setSelfLeaveForm((current) => ({ ...current, reason: '' }));
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsSelfLeaveSubmitting(false);
    }
  }

  async function handleSelfOnboardingTaskComplete(taskId, signatureName = '') {
    if (!selfProfile?.onboarding_handoff?.id) {
      setError('No onboarding handoff is available for this account.');
      return;
    }
    setIsSelfOnboardingSubmitting(true);
    setError('');
    try {
      const task = selfProfile?.onboarding_tasks?.find((item) => String(item.id) === String(taskId));
      const path = task?.is_required
        ? `/api/recruiting/onboarding-handoffs/${selfProfile.onboarding_handoff.id}/tasks/${taskId}/acknowledge`
        : `/api/recruiting/onboarding-handoffs/${selfProfile.onboarding_handoff.id}/tasks/${taskId}/complete`;
      await api.post(
        path,
        task?.is_required ? { signature_name: signatureName } : {},
        authHeaders(token),
      );
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsSelfOnboardingSubmitting(false);
    }
  }

  async function handleSelfOnboardingDocumentUpload(event) {
    event.preventDefault();
    if (!selfProfile?.onboarding_handoff?.id) {
      setError('No onboarding handoff is available for this account.');
      return;
    }
    if (!selfOnboardingDocumentForm.file) {
      setError('Choose a document file to upload.');
      return;
    }
    setIsSelfOnboardingSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('document_name', selfOnboardingDocumentForm.document_name);
      if (selfOnboardingDocumentForm.task_id) {
        formData.append('task_id', selfOnboardingDocumentForm.task_id);
      }
      formData.append('file', selfOnboardingDocumentForm.file);
      await api.post(
        `/api/recruiting/onboarding-handoffs/${selfProfile.onboarding_handoff.id}/documents`,
        formData,
        authHeaders(token),
      );
      setSelfOnboardingDocumentForm({ document_name: '', task_id: '', file: null });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsSelfOnboardingSubmitting(false);
    }
  }

  async function handleManagerLeaveDecision(leaveRequestId, decision) {
    setError('');
    try {
      await api.post(`/api/manager/leave/requests/${leaveRequestId}/${decision}`, {}, authHeaders(token));
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function handleSelfAttendanceAction(action) {
    setIsSelfAttendanceSubmitting(true);
    setError('');
    try {
      await api.post(`/api/self/attendance/${action}`, {}, authHeaders(token));
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsSelfAttendanceSubmitting(false);
    }
  }

  async function handleManagerTimesheetDecision(timesheetId, decision) {
    setError('');
    try {
      await api.post(`/api/manager/attendance/timesheets/${timesheetId}/${decision}`, {}, authHeaders(token));
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function handleSelfPerformanceGoalProgress(goalId, progress, statusValue) {
    setIsSelfPerformanceSubmitting(true);
    setError('');
    try {
      await api.post(
        `/api/self/performance/goals/${goalId}/progress`,
        { progress: Number(progress || 0), status: statusValue || 'in_progress' },
        authHeaders(token),
      );
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsSelfPerformanceSubmitting(false);
    }
  }

  async function handleSelfPerformanceReviewAcknowledge(reviewId, employeeComments) {
    setIsSelfPerformanceSubmitting(true);
    setError('');
    try {
      await api.post(
        `/api/self/performance/reviews/${reviewId}/acknowledge`,
        { employee_comments: employeeComments || null },
        authHeaders(token),
      );
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsSelfPerformanceSubmitting(false);
    }
  }

  async function handleCreatePerformanceGoal(event) {
    event.preventDefault();
    setIsPerformanceSubmitting(true);
    setError('');
    try {
      await api.post(
        '/api/manager/performance/goals',
        {
          employee_id: Number(performanceGoalForm.employee_id),
          cycle_id: performanceGoalForm.cycle_id ? Number(performanceGoalForm.cycle_id) : null,
          title: performanceGoalForm.title,
          metric: performanceGoalForm.metric || null,
          target_value: performanceGoalForm.target_value || null,
          weight: Number(performanceGoalForm.weight || 0),
          due_date: performanceGoalForm.due_date || null,
          description: performanceGoalForm.description || null,
        },
        authHeaders(token),
      );
      setPerformanceGoalForm({ employee_id: '', cycle_id: '', title: '', metric: '', target_value: '', weight: '25', due_date: '', description: '' });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsPerformanceSubmitting(false);
    }
  }

  async function handleSubmitPerformanceReview(event) {
    event.preventDefault();
    setIsPerformanceSubmitting(true);
    setError('');
    try {
      await api.post(
        '/api/manager/performance/reviews',
        {
          employee_id: Number(performanceReviewForm.employee_id),
          cycle_id: Number(performanceReviewForm.cycle_id),
          overall_rating: Number(performanceReviewForm.overall_rating || 0),
          manager_comments: performanceReviewForm.manager_comments,
          status: 'submitted',
        },
        authHeaders(token),
      );
      setPerformanceReviewForm({ employee_id: '', cycle_id: '', overall_rating: '3', manager_comments: '' });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsPerformanceSubmitting(false);
    }
  }

  async function handleCreatePerformanceCycle(event) {
    event.preventDefault();
    setIsPerformanceCycleSubmitting(true);
    setError('');
    try {
      await api.post(
        '/api/performance/cycles',
        performanceCycleForm,
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setPerformanceCycleForm({ name: '', period_start: '2026-01-01', period_end: '2026-12-31', status: 'active' });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsPerformanceCycleSubmitting(false);
    }
  }

  async function handleCreateCompensationCycle(event) {
    event.preventDefault();
    setIsCompensationCycleSubmitting(true);
    setError('');
    try {
      await api.post(
        '/api/compensation/cycles',
        {
          ...compensationCycleForm,
          budget_amount: Number(compensationCycleForm.budget_amount || 0),
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setCompensationCycleForm({ name: '', effective_date: '2026-07-01', budget_amount: 0, status: 'active' });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsCompensationCycleSubmitting(false);
    }
  }

  async function handleCreateCompensationAdjustment(event) {
    event.preventDefault();
    setIsCompensationSubmitting(true);
    setError('');
    try {
      await api.post(
        '/api/manager/compensation/adjustments',
        {
          employee_id: Number(compensationAdjustmentForm.employee_id),
          cycle_id: compensationAdjustmentForm.cycle_id ? Number(compensationAdjustmentForm.cycle_id) : null,
          proposed_basic_salary: Number(compensationAdjustmentForm.proposed_basic_salary || 0),
          proposed_taxable_allowance: Number(compensationAdjustmentForm.proposed_taxable_allowance || 0),
          lump_sum_bonus: Number(compensationAdjustmentForm.lump_sum_bonus || 0),
          rationale: compensationAdjustmentForm.rationale || null,
        },
        authHeaders(token),
      );
      setCompensationAdjustmentForm({ employee_id: '', cycle_id: '', proposed_basic_salary: '', proposed_taxable_allowance: 0, lump_sum_bonus: 0, rationale: '' });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsCompensationSubmitting(false);
    }
  }

  async function handleCompensationAdjustmentDecision(adjustmentId, decision) {
    setError('');
    try {
      const pathDecision = decision === 'apply' ? 'apply' : decision;
      await api.post(
        `/api/compensation/adjustments/${adjustmentId}/${pathDecision}`,
        decision === 'apply' ? {} : { decision_reason: '' },
        authHeaders(token),
      );
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function handleCreateTrainingCourse(event) {
    event.preventDefault();
    setIsTrainingCourseSubmitting(true);
    setError('');
    try {
      await api.post(
        '/api/learning/courses',
        {
          ...trainingCourseForm,
          description: trainingCourseForm.description || null,
          due_days_after_assignment: trainingCourseForm.due_days_after_assignment === '' ? null : Number(trainingCourseForm.due_days_after_assignment),
          renewal_months: trainingCourseForm.renewal_months === '' ? null : Number(trainingCourseForm.renewal_months),
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setTrainingCourseForm({ title: '', category: 'compliance', description: '', required_by_default: true, due_days_after_assignment: '30', renewal_months: '12', is_active: true });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsTrainingCourseSubmitting(false);
    }
  }

  async function handleCreateTrainingAssignment(event) {
    event.preventDefault();
    setIsTrainingAssignmentSubmitting(true);
    setError('');
    try {
      await api.post(
        '/api/learning/assignments',
        {
          course_id: Number(trainingAssignmentForm.course_id),
          employee_id: Number(trainingAssignmentForm.employee_id),
          due_date: trainingAssignmentForm.due_date || null,
          notes: trainingAssignmentForm.notes || null,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setTrainingAssignmentForm({ course_id: '', employee_id: '', due_date: '', notes: '' });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsTrainingAssignmentSubmitting(false);
    }
  }

  async function handleUploadEmployeeDocument(event) {
    event.preventDefault();
    if (!employeeDocumentForm.file) {
      setError(t('chooseFile'));
      return;
    }
    setIsEmployeeDocumentSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('employee_id', employeeDocumentForm.employee_id);
      formData.append('document_name', employeeDocumentForm.document_name);
      formData.append('category', employeeDocumentForm.category || 'general');
      formData.append('issued_at', employeeDocumentForm.issued_at || '');
      formData.append('expires_at', employeeDocumentForm.expires_at || '');
      formData.append('requires_acknowledgement', String(Boolean(employeeDocumentForm.requires_acknowledgement)));
      formData.append('acknowledgement_statement', employeeDocumentForm.acknowledgement_statement || '');
      formData.append('acknowledgement_version', employeeDocumentForm.acknowledgement_version || '');
      formData.append('file', employeeDocumentForm.file);
      await api.post('/api/employee-documents', formData, authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined));
      setEmployeeDocumentForm({ employee_id: '', document_name: '', category: 'policy', issued_at: '', expires_at: '', requires_acknowledgement: false, acknowledgement_statement: '', acknowledgement_version: 'v1', file: null });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsEmployeeDocumentSubmitting(false);
    }
  }

  async function handleSelfTrainingComplete(assignmentId, signatureName) {
    setIsSelfOnboardingSubmitting(true);
    setError('');
    try {
      await api.post(`/api/self/learning/assignments/${assignmentId}/complete`, { signature_name: signatureName }, authHeaders(token));
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsSelfOnboardingSubmitting(false);
    }
  }

  async function handleSelfEmployeeDocumentAcknowledge(documentId, signatureName) {
    setIsSelfOnboardingSubmitting(true);
    setError('');
    try {
      await api.post(`/api/self/documents/${documentId}/acknowledge`, { signature_name: signatureName }, authHeaders(token));
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsSelfOnboardingSubmitting(false);
    }
  }

  async function downloadEmployeeDocument(documentId, fileName) {
    try {
      const response = await api.get(`/api/employee-documents/${documentId}/download`, {
        ...authHeaders(token),
        params: activeCompanyId ? { company_id: activeCompanyId } : undefined,
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'employee-document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function downloadSelfEmployeeDocument(documentId, fileName) {
    try {
      const response = await api.get(`/api/self/documents/${documentId}/download`, {
        ...authHeaders(token),
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'employee-document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  function buildRequestParams(extraParams = {}) {
    return Object.fromEntries(
      Object.entries({
        payroll_month: form.payroll_month,
        ...(activeCompanyId ? { company_id: activeCompanyId } : {}),
        ...extraParams,
      }).filter(([, value]) => value !== undefined && value !== null && value !== ''),
    );
  }

  async function downloadFile(path, filename, contentType, extraParams = {}) {
    try {
      const response = await api.get(path, {
        ...authHeaders(token),
        params: buildRequestParams(extraParams),
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(
        new Blob([response.data], {
          type: contentType,
        }),
      );
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function downloadWorkbook(path, filename, extraParams = {}) {
    await downloadFile(path, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', extraParams);
  }

  async function loadEmployeeBenefits(employeeId) {
    try {
      const response = await api.get(
        `/api/benefits/employee/${employeeId}`,
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setEmployeeBenefits(response.data);
    } catch (submissionError) {
      setEmployeeBenefits([]);
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function loadTimesheets(employeeId) {
    try {
      const params = canSwitchCompanies && activeCompanyId ? { company_id: activeCompanyId } : undefined;
      const response = await api.get(`/api/timesheets/${employeeId}`, authHeaders(token, params));
      setTimesheets(response.data);
    } catch (submissionError) {
      setTimesheets([]);
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function handleCreateBenefitType(event) {
    event.preventDefault();
    setIsBenefitTypeSubmitting(true);
    setError('');

    try {
      await api.post(
        '/api/benefits/types',
        {
          ...benefitTypeForm,
          amount: Number(benefitTypeForm.amount || 0),
          formula: benefitTypeForm.formula || null,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setBenefitTypeForm({ name: '', amount: 0, is_taxable: true, formula: '' });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsBenefitTypeSubmitting(false);
    }
  }

  async function handleAssignBenefit(event) {
    event.preventDefault();
    setIsEmployeeBenefitSubmitting(true);
    setError('');

    try {
      await api.post(
        '/api/benefits/employee',
        {
          employee_id: Number(employeeBenefitForm.employee_id),
          benefit_type_id: Number(employeeBenefitForm.benefit_type_id),
          amount: Number(employeeBenefitForm.amount || 0),
          start_date: employeeBenefitForm.start_date || null,
          end_date: employeeBenefitForm.end_date || null,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      await loadEmployeeBenefits(Number(employeeBenefitForm.employee_id));
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsEmployeeBenefitSubmitting(false);
    }
  }

  async function handleSaveReport(event) {
    event.preventDefault();
    setIsReportSubmitting(true);
    setError('');

    try {
      const filters = JSON.parse(savedReportForm.filters || '{}');
      const columns = savedReportForm.columns
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
      await api.post(
        '/api/reports/save',
        {
          name: savedReportForm.name,
          filters,
          columns,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setSavedReportForm({ name: '', filters: '{"month":"2026-05"}', columns: 'employee_name, net_pay' });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsReportSubmitting(false);
    }
  }

  async function handleCreateRequisition(event) {
    event.preventDefault();
    setIsRecruitingSubmitting(true);
    setError('');

    try {
      await api.post(
        '/api/recruiting/requisitions',
        {
          ...recruitingForm,
          department_id: recruitingForm.department_id ? Number(recruitingForm.department_id) : null,
          position_id: recruitingForm.position_id ? Number(recruitingForm.position_id) : null,
          headcount: Number(recruitingForm.headcount || 1),
          budgeted_salary_min: Number(recruitingForm.budgeted_salary_min || 0),
          budgeted_salary_max: Number(recruitingForm.budgeted_salary_max || 0),
          target_start_date: recruitingForm.target_start_date || null,
          description: recruitingForm.description || null,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setRecruitingForm({
        slug: '',
        title: '',
        department_id: '',
        position_id: '',
        employment_type: 'full_time',
        location: '',
        headcount: 1,
        budgeted_salary_min: 0,
        budgeted_salary_max: 0,
        target_start_date: '',
        description: '',
      });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsRecruitingSubmitting(false);
    }
  }

  async function handleTransitionRequisition(requisitionId, action) {
    setError('');
    try {
      await api.post(
        `/api/recruiting/requisitions/${requisitionId}/${action}`,
        {},
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function handleCreateCandidate(event) {
    event.preventDefault();
    setIsCandidateSubmitting(true);
    setError('');

    try {
      await api.post(
        '/api/recruiting/candidates',
        {
          ...candidateForm,
          phone: candidateForm.phone || null,
          source: candidateForm.source || null,
          current_company: candidateForm.current_company || null,
          current_title: candidateForm.current_title || null,
          resume_url: candidateForm.resume_url || null,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setCandidateForm({
        full_name: '',
        email: '',
        phone: '',
        source: '',
        current_company: '',
        current_title: '',
        resume_url: '',
      });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsCandidateSubmitting(false);
    }
  }

  async function handleCreateApplication(event) {
    event.preventDefault();
    setIsApplicationSubmitting(true);
    setError('');

    try {
      await api.post(
        `/api/recruiting/requisitions/${applicationForm.requisition_id}/applications`,
        {
          candidate_id: Number(applicationForm.candidate_id),
          assigned_recruiter_user_id: null,
          notes_summary: applicationForm.notes_summary || null,
          score: applicationForm.score === '' ? null : Number(applicationForm.score),
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setApplicationForm({ requisition_id: '', candidate_id: '', score: '', notes_summary: '' });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsApplicationSubmitting(false);
    }
  }

  async function handleMoveApplicationStage(event) {
    event.preventDefault();
    setIsApplicationStageSubmitting(true);
    setError('');
    try {
      await api.post(
        `/api/recruiting/applications/${applicationStageForm.application_id}/move-stage`,
        {
          stage: applicationStageForm.stage,
          score: applicationStageForm.score === '' ? null : Number(applicationStageForm.score),
          notes_summary: applicationStageForm.notes_summary || null,
          interview_scheduled_at: applicationStageForm.interview_scheduled_at || null,
          evaluator_notes: applicationStageForm.evaluator_notes || null,
          rejection_reason: applicationStageForm.rejection_reason || null,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setApplicationStageForm({
        application_id: '',
        stage: 'screening',
        score: '',
        notes_summary: '',
        interview_scheduled_at: '',
        evaluator_notes: '',
        rejection_reason: '',
      });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsApplicationStageSubmitting(false);
    }
  }

  async function handleCreateOffer(event) {
    event.preventDefault();
    setIsOfferSubmitting(true);
    setError('');

    try {
      await api.post(
        `/api/recruiting/applications/${offerForm.application_id}/offers`,
        {
          compensation_amount: Number(offerForm.compensation_amount || 0),
          currency: offerForm.currency || 'ETB',
          proposed_start_date: offerForm.proposed_start_date || null,
          expires_at: offerForm.expires_at || null,
          notes_summary: offerForm.notes_summary || null,
          message: offerForm.message || null,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setOfferForm({
        application_id: '',
        compensation_amount: '',
        currency: 'ETB',
        proposed_start_date: '',
        expires_at: '',
        notes_summary: '',
        message: '',
      });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsOfferSubmitting(false);
    }
  }

  async function handleOfferAction(offerId, action) {
    setError('');
    try {
      const pathByAction = {
        requestApproval: 'request-send-approval',
        approveSend: 'approve-send',
        rejectSend: 'reject-send',
        accept: 'accept',
        decline: 'decline',
      };
      await api.post(
        `/api/recruiting/offers/${offerId}/${pathByAction[action]}`,
        {},
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function downloadOfferLetterPdf(offerId, candidateName) {
    setError('');
    try {
      const response = await api.get(`/api/recruiting/offers/${offerId}/letter.pdf`, {
        ...authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${(candidateName || 'offer-letter').replaceAll(/\s+/g, '-').toLowerCase()}-offer-letter.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function loadOnboardingDetails(handoffId, sessionToken = token, companyId = activeCompanyId) {
    if (!handoffId) {
      setOnboardingTasks([]);
      setOnboardingDocuments([]);
      return;
    }
    const companyParams = companyId ? { company_id: companyId } : undefined;
    const [tasksResponse, documentsResponse] = await Promise.all([
      api.get(`/api/recruiting/onboarding-handoffs/${handoffId}/tasks`, authHeaders(sessionToken, companyParams)),
      api.get(`/api/recruiting/onboarding-handoffs/${handoffId}/documents`, authHeaders(sessionToken, companyParams)),
    ]);
    setOnboardingTasks(tasksResponse.data);
    setOnboardingDocuments(documentsResponse.data);
  }

  async function handleSelectOnboardingHandoff(handoffId) {
    setSelectedOnboardingHandoffId(handoffId);
    setOnboardingTaskForm({ title: '', due_date: '' });
    setOnboardingDocumentForm({ document_name: '', task_id: '', file: null });
    const selected = onboardingHandoffs.find((item) => String(item.id) === String(handoffId));
    setOnboardingUserForm({
      username: '',
      password: '',
      full_name: selected?.candidate_name || '',
    });
    if (!handoffId) {
      setOnboardingTasks([]);
      setOnboardingDocuments([]);
      return;
    }
    setError('');
    try {
      await loadOnboardingDetails(handoffId);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function handleOnboardingHandoffAction(handoffId, action) {
    setError('');
    try {
      const pathByAction = {
        start: 'start',
        complete: 'complete',
      };
      await api.post(
        `/api/recruiting/onboarding-handoffs/${handoffId}/${pathByAction[action]}`,
        {},
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      await loadDashboard();
      if (String(selectedOnboardingHandoffId) === String(handoffId)) {
        await loadOnboardingDetails(handoffId);
      }
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function handleCreateOnboardingTask(event) {
    event.preventDefault();
    if (!selectedOnboardingHandoffId) {
      setError('Select an onboarding handoff first.');
      return;
    }
    setIsOnboardingTaskSubmitting(true);
    setError('');
    try {
      await api.post(
        `/api/recruiting/onboarding-handoffs/${selectedOnboardingHandoffId}/tasks`,
        {
          title: onboardingTaskForm.title,
          due_date: onboardingTaskForm.due_date || null,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setOnboardingTaskForm({ title: '', due_date: '' });
      await loadOnboardingDetails(selectedOnboardingHandoffId);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsOnboardingTaskSubmitting(false);
    }
  }

  async function handleCompleteOnboardingTask(taskId) {
    if (!selectedOnboardingHandoffId) {
      return;
    }
    setError('');
    try {
      await api.post(
        `/api/recruiting/onboarding-handoffs/${selectedOnboardingHandoffId}/tasks/${taskId}/complete`,
        {},
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      await loadOnboardingDetails(selectedOnboardingHandoffId);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function handleProvisionOnboardingUser(event) {
    event.preventDefault();
    if (!selectedOnboardingHandoffId) {
      setError('Select an onboarding handoff first.');
      return;
    }
    setIsOnboardingUserSubmitting(true);
    setError('');
    try {
      await api.post(
        `/api/recruiting/onboarding-handoffs/${selectedOnboardingHandoffId}/provision-user`,
        {
          username: onboardingUserForm.username,
          password: onboardingUserForm.password,
          full_name: onboardingUserForm.full_name || null,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setOnboardingUserForm((current) => ({ ...current, password: '' }));
      await loadDashboard();
      await loadOnboardingDetails(selectedOnboardingHandoffId);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsOnboardingUserSubmitting(false);
    }
  }

  async function handleUploadOnboardingDocument(event) {
    event.preventDefault();
    if (!selectedOnboardingHandoffId) {
      setError('Select an onboarding handoff first.');
      return;
    }
    if (!onboardingDocumentForm.file) {
      setError('Choose a document file to upload.');
      return;
    }
    setIsOnboardingDocumentSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('document_name', onboardingDocumentForm.document_name);
      if (onboardingDocumentForm.task_id) {
        formData.append('task_id', onboardingDocumentForm.task_id);
      }
      formData.append('file', onboardingDocumentForm.file);
      await api.post(
        `/api/recruiting/onboarding-handoffs/${selectedOnboardingHandoffId}/documents`,
        formData,
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setOnboardingDocumentForm({ document_name: '', task_id: '', file: null });
      await loadOnboardingDetails(selectedOnboardingHandoffId);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsOnboardingDocumentSubmitting(false);
    }
  }

  async function downloadOnboardingDocument(documentId, fileName) {
    setError('');
    try {
      const response = await api.get(`/api/recruiting/onboarding-documents/${documentId}/download`, {
        ...authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'onboarding-document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  async function handleSaveOfferLetterTemplate(event) {
    event.preventDefault();
    setIsOfferTemplateSubmitting(true);
    setError('');
    try {
      await api.put(
        '/api/recruiting/offer-letter-template',
        {
          document_title: offerLetterTemplate.document_title,
          opening_paragraph: offerLetterTemplate.opening_paragraph,
          body_paragraph: offerLetterTemplate.body_paragraph,
          closing_paragraph: offerLetterTemplate.closing_paragraph,
          signatory_name: offerLetterTemplate.signatory_name || null,
          signatory_title: offerLetterTemplate.signatory_title || null,
        },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsOfferTemplateSubmitting(false);
    }
  }

  async function handleSaveOnboardingChecklistTemplates(event) {
    event.preventDefault();
    setIsOnboardingTemplateSubmitting(true);
    setError('');
    try {
      const items = onboardingChecklistTemplates
        .map((item, index) => ({
          title: item.title,
          due_days_offset: item.due_days_offset === '' || item.due_days_offset === null || typeof item.due_days_offset === 'undefined'
            ? null
            : Number(item.due_days_offset),
          sort_order: index,
          is_required: Boolean(item.is_required),
          acknowledgement_statement: item.acknowledgement_statement?.trim() || null,
          acknowledgement_version: item.acknowledgement_version?.trim() || null,
        }))
        .filter((item) => item.title.trim());
      await api.put(
        '/api/recruiting/onboarding-checklist-templates',
        { items },
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsOnboardingTemplateSubmitting(false);
    }
  }

  async function handleCreateApiKey(event) {
    event.preventDefault();
    setIsApiKeySubmitting(true);
    setError('');

    try {
      const response = await api.post(
        '/api/admin/api-keys',
        apiKeyForm,
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      setGeneratedApiKey(response.data.key);
      setApiKeyForm({ name: '' });
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsApiKeySubmitting(false);
    }
  }

  async function handleCreateLicenseKey(event) {
    event.preventDefault();
    setIsLicenseSubmitting(true);
    setError('');

    try {
      const response = await api.post(
        '/api/admin/licenses',
        {
          company_id: Number(licenseForm.company_id),
          seats: Number(licenseForm.seats || 1),
          expires_at: licenseForm.expires_at || null,
          notes: licenseForm.notes || null,
        },
        authHeaders(token),
      );
      setGeneratedLicenseKey(response.data.key);
      setLicenseForm((current) => ({ ...current, seats: '25', expires_at: '', notes: '' }));
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsLicenseSubmitting(false);
    }
  }

  function addOneYearToIsoDate(value) {
    const baseDate = value ? new Date(`${value}T00:00:00`) : new Date();
    baseDate.setFullYear(baseDate.getFullYear() + 1);
    return baseDate.toISOString().slice(0, 10);
  }

  async function handleLicenseLifecycleAction(license, action) {
    setIsLicenseSubmitting(true);
    setError('');

    try {
      const today = new Date().toISOString().slice(0, 10);
      const nextPayload = {
        status: license.status,
        seats: Number(license.seats || 1),
        expires_at: license.expires_at || null,
        notes: license.notes || null,
      };
      if (action === 'activate') {
        nextPayload.status = 'active';
      }
      if (action === 'suspend') {
        nextPayload.status = 'suspended';
      }
      if (action === 'expire') {
        nextPayload.status = 'expired';
        nextPayload.expires_at = today;
      }
      if (action === 'renew') {
        nextPayload.status = 'active';
        nextPayload.expires_at = addOneYearToIsoDate(license.expires_at);
      }

      await api.put(`/api/admin/licenses/${license.id}`, nextPayload, authHeaders(token));
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsLicenseSubmitting(false);
    }
  }

  async function handleEnableMfa() {
    setIsMfaSubmitting(true);
    setError('');

    try {
      await api.post('/api/self-service/enable-mfa', {}, authHeaders(token));
      await loadDashboard();
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsMfaSubmitting(false);
    }
  }

  async function handleTimesheetImport() {
    if (!timesheetImportFile) {
      setError(t('chooseFile'));
      return;
    }
    setIsTimesheetImporting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', timesheetImportFile);
      await api.post(
        '/api/timesheets/import/zkteco',
        formData,
        authHeaders(token, activeCompanyId ? { company_id: activeCompanyId } : undefined),
      );
      if (timesheetEmployeeId) {
        await loadTimesheets(Number(timesheetEmployeeId));
      }
      setTimesheetImportFile(null);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    } finally {
      setIsTimesheetImporting(false);
    }
  }

  function exportHistoryCsv() {
    if (!history.length) {
      return;
    }

    const headers = [
      'Employee',
      'Month',
      'Basic Salary',
      'Taxable Allowance',
      'Non-Taxable Allowance',
      'Other Deductions',
      'Gross Pay',
      'Income Tax',
      'Employee Pension',
      'Employer Pension',
      'Net Pay',
      'Created At',
    ];
    const rows = history.map((item) => [
      item.employee_name,
      item.payroll_month || '',
      item.basic_salary,
      item.taxable_allowance,
      item.non_taxable_allowance,
      item.other_deductions,
      item.gross_pay,
      item.income_tax,
      item.pension_employee,
      item.pension_employer,
      item.net_pay,
      item.created_at,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `afrihr-history-${form.payroll_month || 'current'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function downloadPayslipPdf(recordId, employeeName) {
    if (!recordId) {
      setError(t('exportUnavailable'));
      return;
    }

    try {
      const response = await api.get(`/api/payroll/history/${recordId}/payslip.pdf`, {
        ...authHeaders(token),
        params: buildRequestParams({ lang: activeLanguage, payroll_month: undefined }),
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${(employeeName || 'payslip').replaceAll(/\s+/g, '-').toLowerCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (submissionError) {
      setError(submissionError.response?.data?.detail || submissionError.message);
    }
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function updateEmployeeField(field, value) {
    setEmployeeForm((current) => ({ ...current, [field]: value }));
  }

  function signOut() {
    authSignOut();
    setCompanies([]);
    setClientPortfolio([]);
    setSelectedCompanyId('');
    setDepartments([]);
    setPositions([]);
    setDashboardMetrics(null);
    setEmployees([]);
    setUsers([]);
    setAuditLogs([]);
    setHistory([]);
    setBenefitTypes([]);
    setEmployeeBenefits([]);
    setSavedReports([]);
    setRequisitions([]);
    setCandidates([]);
    setApplications([]);
    setApplicationStageForm({
      application_id: '',
      stage: 'screening',
      score: '',
      notes_summary: '',
      interview_scheduled_at: '',
      evaluator_notes: '',
      rejection_reason: '',
    });
    setApiKeys([]);
    setLicenses([]);
    setTimesheets([]);
    setGeneratedApiKey('');
    setGeneratedLicenseKey('');
    setResult(null);
    setBatchResults([]);
    setLeaveTypes([]);
    setLeaveRequests([]);
    setLoans([]);
    setSelfProfile(null);
    setSelfHistory([]);
    setSelfLeaveRequests([]);
    setSelfAttendance([]);
    setSelfPerformanceGoals([]);
    setSelfPerformanceReviews([]);
    setSelfLoans([]);
    setManagerLeaveRequests([]);
    setManagerTeamSummary(null);
    setManagerTimesheets([]);
    setManagerTeamMembers([]);
    setPerformanceCycles([]);
    setAllPerformanceCycles([]);
    setManagerPerformanceGoals([]);
    setManagerPerformanceReviews([]);
    setCompensationCycles([]);
    setCompensationAdjustments([]);
    setManagerCompensationAdjustments([]);
    setTrainingCourses([]);
    setTrainingAssignments([]);
    setEmployeeDocuments([]);
    setEditingUserId(null);
  }

  function resetUserForm() {
    setEditingUserId(null);
    setUserForm({ username: '', full_name: '', password: '', role: 'employee', employee_id: '', primary_company_id: '', company_ids: [] });
  }

  function startEditingUser(user) {
    setEditingUserId(user.id);
    setUserForm({
      username: user.username,
      full_name: user.full_name,
      password: '',
      role: user.role,
      employee_id: user.employee_id ? String(user.employee_id) : '',
      primary_company_id: user.company_id ? String(user.company_id) : '',
      company_ids: (user.assigned_company_ids || []).map((companyId) => String(companyId)),
    });
  }

  async function focusPortfolioCompany(companyId, sectionId = null) {
    const nextCompanyId = String(companyId);
    setSelectedCompanyId(nextCompanyId);
    setEmployeeBenefits([]);
    setTimesheets([]);
    await loadDashboard(token, currentUser, nextCompanyId);
    if (sectionId) {
      window.requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  function exportAuditCsv() {
    if (!auditLogs.length) {
      return;
    }

    const headers = ['Created At', 'Action', 'Entity', 'Record ID', 'User ID'];
    const rows = auditLogs.map((item) => [item.created_at, item.action, item.entity_name, item.record_id || '', item.user_id || '']);
    const csv = [headers, ...rows]
      .map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `afrihr-audit-${activeCompanyId || 'portfolio'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function renderLanguageSelector() {
    return (
      <label className="language-selector">
        <span>{t('appTitle')}</span>
        <select value={i18n.resolvedLanguage || i18n.language} onChange={(event) => i18n.changeLanguage(event.target.value)}>
          <option value="en">{t('english')}</option>
          <option value="am">{t('amharic')}</option>
          <option value="om">{t('oromo')}</option>
        </select>
      </label>
    );
  }

  async function handleCompanySwitch(event) {
    const nextCompanyId = event.target.value;
    setSelectedCompanyId(nextCompanyId);
    setEmployeeBenefits([]);
    setTimesheets([]);
    setGeneratedApiKey('');
    setEmployeeBenefitForm((current) => ({ ...current, employee_id: '' }));
    setTimesheetEmployeeId('');
    await loadDashboard(token, currentUser, nextCompanyId);
  }

  function renderCompanySelector() {
    if (!canSwitchCompanies || !companies.length) {
      return null;
    }
    return (
      <label className="language-selector company-selector">
        <span>{t('activeCompany')}</span>
        <select value={selectedCompanyId} onChange={(event) => void handleCompanySwitch(event)}>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>{company.name}</option>
          ))}
        </select>
      </label>
    );
  }

  if (!currentUser) {
    // ProtectedRoute should have redirected before reaching this component;
    // render nothing as a defensive fallback while the redirect completes.
    return null;
  }

  return (
    <main className="shell">
      <section className="hero">
        <div>
          <BrandLogo />
          <h1>{t('heroTitle')}</h1>
          <p className="lede">{t('heroDescription')}</p>
        </div>
        <div className="hero-panel">
          {renderLanguageSelector()}
          {renderCompanySelector()}
          <p className="hero-user">{t('signedInAs')} {currentUser.full_name}</p>
          <p className="hero-role">{t('role')}: {formatRoleLabel(currentUser.role)}</p>
          {canSwitchCompanies && companies.length ? <p className="hero-company">{t('activeCompany')}: {companies.find((company) => company.id === Number(selectedCompanyId))?.name || t('appTitle')}</p> : null}
          <button type="button" className="ghost-button" onClick={signOut}>
            {t('signOut')}
          </button>
          <button
            type="button"
            className="ghost-button theme-toggle"
            onClick={() => setTheme((current) => (current === 'dark' ? 'light' : current === 'light' ? 'auto' : 'dark'))}
            title={`Theme: ${theme}`}
          >
            {theme === 'dark' ? '☀️ Light' : theme === 'light' ? '🖥️ Auto' : '🌙 Dark'}
          </button>
        </div>
      </section>

      {navPages.length > 1 ? (
        <nav className="app-nav" aria-label="Primary">
          {navPages.map((page) => (
            <button
              key={page.id}
              type="button"
              className={`nav-link${activePage === page.id ? ' active' : ''}`}
              onClick={() => navigate(`/${page.id}`)}
            >
              {page.label}
            </button>
          ))}
        </nav>
      ) : null}

      {canViewPortfolio && activePage === 'overview' ? (
      <ClientPortfolioCard
        clients={clientPortfolio}
        activeLanguage={activeLanguage}
        onFocusCompany={(companyId, anchor) => void focusPortfolioCompany(companyId, anchor)}
        onExportErca={downloadWorkbook}
        onExportCostCenters={downloadWorkbook}
        onExportGl={downloadWorkbook}
        onExportBankEft={downloadFile}
      />
      ) : null}

      <section className="grid dashboard-grid">
        {canViewEnterpriseOps && dashboardMetrics && activePage === 'overview' ? (
        <EnterpriseDashboardCard metrics={dashboardMetrics} activeLanguage={activeLanguage} />
        ) : null}

        {canManageEmployees && activePage === 'people' ? (
        <EmployeeManagementCard
          form={employeeForm}
          setForm={setEmployeeForm}
          updateField={updateEmployeeField}
          positions={positions}
          isSubmitting={isEmployeeSubmitting}
          onSubmit={handleCreateEmployee}
          importFile={importFile}
          onImportFileChange={setImportFile}
          onBulkImport={handleBulkImport}
          isImporting={isImporting}
        />
        ) : null}

        {canManageEmployees && activePage === 'people' ? (
        <PositionManagementCard
          form={positionForm}
          setForm={setPositionForm}
          departments={departments}
          isSubmitting={isPositionSubmitting}
          onSubmit={handleCreatePosition}
        />
        ) : null}

        {canViewEnterpriseOps && activePage === 'people' ? (
        <PositionsListCard positions={positions} activeLanguage={activeLanguage} />
        ) : null}

        {canManageEmployees && activePage === 'people' ? (
        <PerformanceCyclesCard
          form={performanceCycleForm}
          setForm={setPerformanceCycleForm}
          cycles={allPerformanceCycles}
          isSubmitting={isPerformanceCycleSubmitting}
          onSubmit={handleCreatePerformanceCycle}
        />
        ) : null}

        {canManageEmployees && (activePage === 'people' || activePage === 'learning') ? (
        <LearningComplianceCard
          courseForm={trainingCourseForm}
          setCourseForm={setTrainingCourseForm}
          assignmentForm={trainingAssignmentForm}
          setAssignmentForm={setTrainingAssignmentForm}
          courses={trainingCourses}
          assignments={trainingAssignments}
          employees={employees}
          isCourseSubmitting={isTrainingCourseSubmitting}
          isAssignmentSubmitting={isTrainingAssignmentSubmitting}
          onCourseSubmit={handleCreateTrainingCourse}
          onAssignmentSubmit={handleCreateTrainingAssignment}
        />
        ) : null}

        {canManageEmployees && (activePage === 'people' || activePage === 'learning') ? (
        <EmployeeDocumentsCard
          form={employeeDocumentForm}
          setForm={setEmployeeDocumentForm}
          documents={employeeDocuments}
          employees={employees}
          isSubmitting={isEmployeeDocumentSubmitting}
          onSubmit={handleUploadEmployeeDocument}
          onDownload={downloadEmployeeDocument}
        />
        ) : null}

        {canManageCompanies && activePage === 'admin' ? (
        <AdminCustomerOnboardingCard
          form={customerOnboardingForm}
          setForm={setCustomerOnboardingForm}
          isSubmitting={isCustomerOnboardingSubmitting}
          onSubmit={handleOnboardCustomer}
        />
        ) : null}

        {canManageCompanies && activePage === 'admin' ? (
        <AdminCompaniesCard
          form={companyForm}
          setForm={setCompanyForm}
          companies={companies}
          isSubmitting={isCompanySubmitting}
          onSubmit={handleCreateCompany}
        />
        ) : null}

        {adminLikeRoles.includes(currentRole) && activePage === 'admin' ? (
        <AdminCompanyBankingCard
          form={companyBankingForm}
          setForm={setCompanyBankingForm}
          banks={banks}
          bankBranches={bankBranches}
          activeCompanyName={activeCompanyName}
          providerApiKeyConfigured={providerApiKeyConfigured}
          isSubmitting={isCompanyBankingSubmitting}
          onSubmit={handleSaveCompanyBanking}
        />
        ) : null}

        {canManageUsers && activePage === 'admin' ? (
        <AdminUsersCard
          form={userForm}
          setForm={setUserForm}
          editingUserId={editingUserId}
          companies={companies}
          employees={employees}
          users={users}
          activeCompanyId={activeCompanyId}
          isSubmitting={isUserSubmitting}
          canAssignSystemAdminRole={isSystemAdmin}
          onSubmit={handleCreateUser}
          onResetForm={resetUserForm}
          onStartEditing={startEditingUser}
        />
        ) : null}

        {canProcessPayroll && activePage === 'payroll' ? (
        <RunPayrollCard
          form={form}
          setForm={setForm}
          updateField={updateField}
          employees={employees}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onBatchRun={handleBatchRun}
          onExportPayslip={() => downloadPayslipPdf(resultRecordId, result?.employee_name)}
          resultRecordId={resultRecordId}
          result={result}
          batchResults={batchResults}
          activeLanguage={activeLanguage}
          error={error}
        />
        ) : null}

        {canManageCompensation && activePage === 'payroll' ? (
        <CompensationPlanningCard
          cycleForm={compensationCycleForm}
          setCycleForm={setCompensationCycleForm}
          cycles={compensationCycles}
          adjustments={compensationAdjustments}
          isSubmitting={isCompensationCycleSubmitting}
          onCycleSubmit={handleCreateCompensationCycle}
          onAdjustmentDecision={handleCompensationAdjustmentDecision}
          activeLanguage={activeLanguage}
        />
        ) : null}

        {canViewEnterpriseOps && activePage === 'people' ? (
        <EmployeesListCard employees={employees} activeLanguage={activeLanguage} />
        ) : null}

        {canViewRecruiting && activePage === 'recruiting' ? (
        <RecruitingPage
          form={recruitingForm}
          setForm={setRecruitingForm}
          candidateForm={candidateForm}
          setCandidateForm={setCandidateForm}
          applicationForm={applicationForm}
          setApplicationForm={setApplicationForm}
          stageForm={applicationStageForm}
          setStageForm={setApplicationStageForm}
          offerForm={offerForm}
          setOfferForm={setOfferForm}
          requisitions={requisitions}
          candidates={candidates}
          applications={applications}
          offers={offers}
          onboardingHandoffs={onboardingHandoffs}
          selectedOnboardingHandoffId={selectedOnboardingHandoffId}
          setSelectedOnboardingHandoffId={handleSelectOnboardingHandoff}
          onboardingTasks={onboardingTasks}
          onboardingDocuments={onboardingDocuments}
          onboardingTaskForm={onboardingTaskForm}
          setOnboardingTaskForm={setOnboardingTaskForm}
          onboardingUserForm={onboardingUserForm}
          setOnboardingUserForm={setOnboardingUserForm}
          onboardingDocumentForm={onboardingDocumentForm}
          setOnboardingDocumentForm={setOnboardingDocumentForm}
          onboardingChecklistTemplates={onboardingChecklistTemplates}
          setOnboardingChecklistTemplates={setOnboardingChecklistTemplates}
          onboardingSummary={onboardingSummary}
          offerLetterTemplate={offerLetterTemplate}
          setOfferLetterTemplate={setOfferLetterTemplate}
          departments={departments}
          positions={positions}
          canManage={canManageRecruiting}
          canApprove={canApproveRecruiting}
          isSubmitting={isRecruitingSubmitting}
          isCandidateSubmitting={isCandidateSubmitting}
          isApplicationSubmitting={isApplicationSubmitting}
          isStageSubmitting={isApplicationStageSubmitting}
          isOfferSubmitting={isOfferSubmitting}
          isOfferTemplateSubmitting={isOfferTemplateSubmitting}
          isOnboardingTemplateSubmitting={isOnboardingTemplateSubmitting}
          isOnboardingTaskSubmitting={isOnboardingTaskSubmitting}
          isOnboardingUserSubmitting={isOnboardingUserSubmitting}
          isOnboardingDocumentSubmitting={isOnboardingDocumentSubmitting}
          onSubmit={handleCreateRequisition}
          onCandidateSubmit={handleCreateCandidate}
          onApplicationSubmit={handleCreateApplication}
          onStageSubmit={handleMoveApplicationStage}
          onOfferSubmit={handleCreateOffer}
          onOfferAction={handleOfferAction}
          onOfferLetterDownload={downloadOfferLetterPdf}
          onOnboardingHandoffAction={handleOnboardingHandoffAction}
          onOnboardingTaskSubmit={handleCreateOnboardingTask}
          onOnboardingTaskComplete={handleCompleteOnboardingTask}
          onOnboardingUserProvision={handleProvisionOnboardingUser}
          onOnboardingDocumentSubmit={handleUploadOnboardingDocument}
          onOnboardingDocumentDownload={downloadOnboardingDocument}
          onOnboardingTemplateSubmit={handleSaveOnboardingChecklistTemplates}
          onOfferTemplateSubmit={handleSaveOfferLetterTemplate}
          onTransition={handleTransitionRequisition}
          activeLanguage={activeLanguage}
        />
        ) : null}

        {canViewFinance && activePage === 'payroll' ? (
        <PayrollHistoryCard
          history={history}
          payrollMonth={form.payroll_month}
          activeLanguage={activeLanguage}
          canRunFinanceExports={canRunFinanceExports}
          showPayoutExport={[...adminLikeRoles, 'finance'].includes(currentUser?.role || '')}
          onExportCsv={exportHistoryCsv}
          onExportErca={() => downloadWorkbook('/api/payroll/reports/erca.xlsx', `erca-${form.payroll_month || 'report'}.xlsx`)}
          onExportPayouts={downloadPayoutCsv}
          onExportCostCenters={() => downloadWorkbook('/api/payroll/reports/cost-centers.xlsx', `cost-centers-${form.payroll_month || 'report'}.xlsx`)}
          onExportPayslip={downloadPayslipPdf}
        />
        ) : null}

        {canViewEnterpriseOps && activePage === 'leave' ? (
        <EnterpriseLeaveQueueCard requests={leaveRequests} />
        ) : null}

        {canViewEnterpriseOps && activePage === 'leave' ? (
        <LoanRegisterCard loans={loans} activeLanguage={activeLanguage} />
        ) : null}

        {canManageEmployees && activePage === 'benefits' ? (
        <BenefitTypesCard
          form={benefitTypeForm}
          setForm={setBenefitTypeForm}
          benefitTypes={benefitTypes}
          isSubmitting={isBenefitTypeSubmitting}
          onSubmit={handleCreateBenefitType}
          activeLanguage={activeLanguage}
        />
        ) : null}

        {canManageEmployees && activePage === 'benefits' ? (
        <EmployeeBenefitsCard
          form={employeeBenefitForm}
          setForm={setEmployeeBenefitForm}
          employees={employees}
          benefitTypes={benefitTypes}
          employeeBenefits={employeeBenefits}
          isSubmitting={isEmployeeBenefitSubmitting}
          onSubmit={handleAssignBenefit}
          activeLanguage={activeLanguage}
        />
        ) : null}

        {canManageEmployees && activePage === 'people' ? (
        <TimesheetsCard
          timesheets={timesheets}
          employees={employees}
          employeeId={timesheetEmployeeId}
          onEmployeeIdChange={setTimesheetEmployeeId}
          importFile={timesheetImportFile}
          onImportFileChange={setTimesheetImportFile}
          onImport={handleTimesheetImport}
          isImporting={isTimesheetImporting}
        />
        ) : null}

        {canViewFinance && activePage === 'reports' ? (
        <ReportsPage
          form={savedReportForm}
          onFormChange={setSavedReportForm}
          reports={savedReports}
          isSubmitting={isReportSubmitting}
          onSubmit={handleSaveReport}
        />
        ) : null}

        {canManageUsers && activePage === 'admin' ? (
        <AdminApiKeysCard
          form={apiKeyForm}
          setForm={setApiKeyForm}
          generatedApiKey={generatedApiKey}
          apiKeys={apiKeys}
          isSubmitting={isApiKeySubmitting}
          onSubmit={handleCreateApiKey}
        />
        ) : null}

        {canManageLicenses && activePage === 'admin' ? (
        <AdminLicensesCard
          form={licenseForm}
          setForm={setLicenseForm}
          companies={companies}
          generatedLicenseKey={generatedLicenseKey}
          licenses={licenses}
          isSubmitting={isLicenseSubmitting}
          onSubmit={handleCreateLicenseKey}
          onLifecycleAction={handleLicenseLifecycleAction}
        />
        ) : null}

        {canUseSelfService && activePage === 'me' ? (
        <SelfProfileCard
          profile={selfProfile}
          currentUser={currentUser}
          activeLanguage={activeLanguage}
          banks={banks}
          bankBranches={selfBankBranches}
          bankingForm={selfBankingForm}
          setBankingForm={setSelfBankingForm}
          onBankingSubmit={handleSaveSelfBanking}
          onEnableMfa={handleEnableMfa}
          onOnboardingDocumentDownload={downloadOnboardingDocument}
          onOnboardingTaskComplete={handleSelfOnboardingTaskComplete}
          onTrainingComplete={handleSelfTrainingComplete}
          onEmployeeDocumentAcknowledge={handleSelfEmployeeDocumentAcknowledge}
          onEmployeeDocumentDownload={downloadSelfEmployeeDocument}
          onOnboardingDocumentUpload={handleSelfOnboardingDocumentUpload}
          selfOnboardingDocumentForm={selfOnboardingDocumentForm}
          setSelfOnboardingDocumentForm={setSelfOnboardingDocumentForm}
          isBankingSubmitting={isSelfBankingSubmitting}
          isSelfOnboardingSubmitting={isSelfOnboardingSubmitting}
          isMfaSubmitting={isMfaSubmitting}
          error={error}
        />
        ) : null}

        {canUseSelfService && activePage === 'me' ? (
        <SelfLeaveRequestCard
          form={selfLeaveForm}
          setForm={setSelfLeaveForm}
          leaveTypes={leaveTypes}
          isSubmitting={isSelfLeaveSubmitting}
          onSubmit={handleSelfLeaveRequest}
        />
        ) : null}

        {canUseSelfService && activePage === 'me' ? (
        <MyLeaveRequestsCard requests={selfLeaveRequests} />
        ) : null}

        {canUseSelfService && activePage === 'me' ? (
        <SelfAttendanceCard
          attendance={selfAttendance}
          onClockIn={() => handleSelfAttendanceAction('clock-in')}
          onClockOut={() => handleSelfAttendanceAction('clock-out')}
          isSubmitting={isSelfAttendanceSubmitting}
        />
        ) : null}

        {canUseSelfService && activePage === 'me' ? (
        <MyPerformanceCard
          goals={selfPerformanceGoals}
          reviews={selfPerformanceReviews}
          onGoalProgress={handleSelfPerformanceGoalProgress}
          onReviewAcknowledge={handleSelfPerformanceReviewAcknowledge}
          isSubmitting={isSelfPerformanceSubmitting}
        />
        ) : null}

        {canUseSelfService && activePage === 'me' ? (
        <MyPayrollHistoryCard history={selfHistory} activeLanguage={activeLanguage} />
        ) : null}

        {canUseSelfService && activePage === 'me' ? (
        <MyLoansCard loans={selfLoans} activeLanguage={activeLanguage} />
        ) : null}

        {canReviewManagerQueues && activePage === 'inbox' ? (
        <InboxPage
          requests={managerLeaveRequests}
          onDecide={handleManagerLeaveDecision}
          teamSummary={managerTeamSummary}
          timesheets={managerTimesheets}
          onTimesheetDecide={handleManagerTimesheetDecision}
          teamMembers={managerTeamMembers}
          performanceCycles={performanceCycles}
          performanceGoals={managerPerformanceGoals}
          performanceReviews={managerPerformanceReviews}
          performanceGoalForm={performanceGoalForm}
          setPerformanceGoalForm={setPerformanceGoalForm}
          performanceReviewForm={performanceReviewForm}
          setPerformanceReviewForm={setPerformanceReviewForm}
          onPerformanceGoalSubmit={handleCreatePerformanceGoal}
          onPerformanceReviewSubmit={handleSubmitPerformanceReview}
          isPerformanceSubmitting={isPerformanceSubmitting}
          compensationCycles={compensationCycles}
          compensationAdjustments={managerCompensationAdjustments}
          compensationAdjustmentForm={compensationAdjustmentForm}
          setCompensationAdjustmentForm={setCompensationAdjustmentForm}
          onCompensationAdjustmentSubmit={handleCreateCompensationAdjustment}
          isCompensationSubmitting={isCompensationSubmitting}
        />
        ) : null}

        {canUseSelfService && activePage === 'me' ? (<NotificationsCard />) : null}

        {(canManageEmployees || canViewEnterpriseOps) && activePage === 'enterprise' ? (<WorkforceAnalyticsCard />) : null}
        {(canManageEmployees || canViewEnterpriseOps) && activePage === 'enterprise' ? (<OrgChartCard />) : null}
        {(canManageEmployees || canViewEnterpriseOps) && activePage === 'enterprise' ? (<ContractsCard />) : null}
        {(canManageEmployees || canViewEnterpriseOps) && activePage === 'enterprise' ? (<OffboardingCard />) : null}
        {(canManageEmployees || canViewEnterpriseOps) && activePage === 'enterprise' ? (<DisciplinaryCard />) : null}
        {(canManageEmployees || canViewEnterpriseOps) && activePage === 'enterprise' ? (<AssetsCard />) : null}
        {(canManageEmployees || canViewEnterpriseOps) && activePage === 'enterprise' ? (<ShiftsCard />) : null}
        {(canManageEmployees || canViewFinance) && activePage === 'enterprise' ? (<ComplianceCard />) : null}

        {(canUseSelfService || canViewFinance) && activePage === 'expenses' ? (
          <ExpensesCard canApprove={canViewFinance || canManageEmployees} />
        ) : null}
      </section>
    </main>
  );
}
